/**
 * Pausable game clock.
 *
 * Gameplay systems mix frame-based timing (frameCountRef) with wall-clock
 * timestamps (Date.now()). Wall time keeps running while the game is paused
 * or the tab is backgrounded, so corpses expired, mage projectiles hit max
 * scale, and item-drop messages vanished during a pause while frame-based
 * logic stood still.
 *
 * gameNow() is a drop-in replacement for Date.now() that freezes while the
 * game loop is not running. All gameplay producers AND consumers of a
 * timestamp must use the same clock - never compare gameNow() against a
 * Date.now() value or vice versa.
 *
 * Input timing (tap detection) and self-contained UI animations should keep
 * using Date.now().
 */

let pausedOffset = 0
let frozenAt: number | null = null

// Fast-forward support. gameNow() advances at `rate` x real time, so both the
// frame-based systems (whose tick count the loop scales by the same rate) and
// every wall-clock consumer (animations, buff durations, effect lifetimes)
// speed up together - scaling only the tick count would have made frame-based
// cooldowns run 2x while gameNow-based durations stayed at 1x.
let rate = 1
// gameNow() value at the moment the rate last changed - the anchor that keeps
// the clock continuous across rate switches. Anchored to Date.now() so the
// clock stays on the epoch scale: persisted run snapshots store gameNow()
// timestamps (tower/summon animation starts), and a restored session must
// read them as "in the past", not as minutes in the future.
let rateAnchorGame = Date.now()
let rateAnchorReal = Date.now()

function realNow(): number {
  return frozenAt !== null ? frozenAt : Date.now()
}

/** Freeze the clock (call when the game loop stops - pause, menus, hidden tab) */
export function freezeGameClock(): void {
  if (frozenAt === null) {
    frozenAt = Date.now()
  }
}

/** Resume the clock (call when the game loop starts running again) */
export function unfreezeGameClock(): void {
  if (frozenAt !== null) {
    pausedOffset += Date.now() - frozenAt
    frozenAt = null
  }
}

/** Current game time in ms - monotonic, frozen while paused, scaled by rate */
export function gameNow(): number {
  const real = realNow() - pausedOffset
  return rateAnchorGame + (real - rateAnchorReal) * rate
}

/** Current fast-forward rate (1 = real time) */
export function getGameClockRate(): number {
  return rate
}

/** Change the fast-forward rate without any jump in gameNow() */
export function setGameClockRate(newRate: number): void {
  if (newRate === rate || newRate <= 0) return
  const real = realNow() - pausedOffset
  rateAnchorGame = rateAnchorGame + (real - rateAnchorReal) * rate
  rateAnchorReal = real
  rate = newRate
}
